import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Plus } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  apiDeleteMonitoringSettingsMonitoringUuid,
  apiPutMonitoringSettingsMonitoringUuid,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { location } from "../../../app/location";
import { useCountries } from "../../../catalogs/countries";
import { Button } from "../../../components/ui-kit/button";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import {
  monitoringTypes,
  patientStatuses,
} from "../../../constants/enum-to-text";
import {
  getCountryOptions,
  getRegionOptions,
  regionDisabled,
  regionPredicate,
} from "../../../utils/filter-presets";
import { assert } from "../../../utils/utils";
import { AddMonitoringSettingsDialog } from "./add-monitoring-settings-dialog";
import { formatDuration, parseDuration } from "./duration-utils";
import { EditMonitoringSettingsDialog } from "./edit-monitoring-settings-dialog";
import { consentTypeValues } from "./monitoring-settings";

export const MonitoringSettingsRoute = observer(
  function MonitoringSettingsRoute(props: { app: AppModel }) {
    const { t } = useLingui();
    const admin = props.app.requireAdminShit();
    const model = admin.monitoringSettings;
    assert(model, "Monitoring settings model not initialized");
    const countries = useCountries();

    const [showAddDialog, setShowAddDialog] = useState(false);

    return (
      <>
        {showAddDialog && (
          <AddMonitoringSettingsDialog
            admin={admin}
            onClose={() => setShowAddDialog(false)}
            onSaved={() => {
              void admin.refreshMonitoringSettings();
            }}
          />
        )}

        <DataTable
          persistState
          editMode="dialog"
          itemKeyField="uuid"
          filterNoun={(count) =>
            t`${plural(count, { one: "setting", other: "settings" })}`
          }
          filterActions={
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => setShowAddDialog(true)}
            >
              <Trans>New monitoring settings</Trans>
            </Button>
          }
          columns={[
            {
              header: <Trans>Monitoring type</Trans>,
              attr: "monitoring_type",
              type: "enum",
              values: monitoringTypes,
              filter: {
                kind: "multi",
                group: t`Settings`,
                options: new Map(Object.entries(monitoringTypes)),
              },
              cell: ({ item }) => item.monitoringTypeText,
            },
            {
              header: <Trans>Country</Trans>,
              attr: "country",
              type: "enum",
              values: countries.countriesEnum,
              filter: {
                kind: "multi",
                group: t`Location`,
                options: getCountryOptions,
              },
              cell: ({ item }) => item.country,
            },
            {
              header: <Trans>Region</Trans>,
              attr: "region",
              filter: {
                kind: "multi",
                group: t`Location`,
                options: (table) => getRegionOptions(table, countries),
                disabled: regionDisabled,
                disabledMessage: t`Select a country first`,
                predicate: regionPredicate,
              },
              cell: ({ item }) => {
                if (!item.country) return item.region;
                const regions = countries.getRegionsEnum(item.country);
                return regions?.[item.region as any] ?? item.region;
              },
            },
            {
              header: <Trans>New patient status</Trans>,
              attr: "new_patient_status",
              type: "enum",
              values: patientStatuses,
            },
            {
              header: <Trans>Auto schedule</Trans>,
              attr: "auto_schedule",
              type: "boolean",
              width: 100,
            },
            {
              header: <Trans>Req. meas. config</Trans>,
              attr: "requires_measurement_config",
              type: "boolean",
              width: 100,
            },
            {
              header: <Trans>Auto alerts</Trans>,
              attr: "auto_alerts",
              type: "boolean",
              width: 100,
            },
            {
              header: <Trans>Consent type</Trans>,
              attr: "consent_type",
              type: "enum",
              values: consentTypeValues,
            },
            {
              header: <Trans>Is monitored</Trans>,
              attr: "is_monitored",
              type: "boolean",
              width: 100,
            },
            {
              header: <Trans>Any new data activate</Trans>,
              attr: "any_new_data_activate",
              type: "boolean",
              width: 100,
            },
            {
              header: <Trans>PPG/ECG activate</Trans>,
              attr: "ppg_ecg_new_data_activate",
              type: "boolean",
              width: 100,
            },
            {
              header: <Trans>Auto deactivate after</Trans>,
              attr: "auto_deactivate_after",
              width: 100,
              cell: ({ item }) =>
                formatDuration(parseDuration(item.auto_deactivate_after)),
            },
            {
              header: <Trans>Status change email</Trans>,
              attr: "status_change_email",
              type: "boolean",
              width: 100,
            },
            {
              header: <Trans>Clinic</Trans>,
              cell: ({ item }) => item.clinicName,
            },
            {
              header: <Trans>Physician</Trans>,
              cell: ({ item }) => item.physicianName,
            },
            {
              header: <Trans>Created</Trans>,
              attr: "created_at",
              type: "datetime",
              sort: true,
            },
            {
              header: <Trans>Updated</Trans>,
              attr: "updated_at",
              type: "datetime",
              sort: true,
            },
          ]}
          model={model}
          renderEditDialog={({ item, onClose, onSave }) => (
            <EditMonitoringSettingsDialog
              item={item}
              admin={admin}
              onClose={onClose}
              onSave={onSave}
            />
          )}
          onDelete={async (item) => {
            await apiDeleteMonitoringSettingsMonitoringUuid({
              path: { uuid: item.uuid },
            });
            location.reload();
          }}
          onEdit={async (patch, item) => {
            await apiPutMonitoringSettingsMonitoringUuid({
              path: { uuid: item.uuid },
              body: patch,
            });
            location.reload();
          }}
          rowsPerPage={10}
        />
      </>
    );
  },
);
